import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  getEmargement,
  signerJournee,
  enregistrerNotesFormateur,
} from '../services/emargementService';
import SignaturePad from './SignaturePad';
import { formatDateTime } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import './Signature.css';

/** 'aaaa-mm-jj' -> « jeudi 8 janvier 2027 ». */
function jourLong(iso) {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** 'aaaa-mm-jj' -> « mar. 14 oct. », pour les puces du bloc de signature. */
function jourCourt(iso) {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// Deux cases par journée, matin et après-midi, comme sur la feuille papier.
const DEMI_JOURNEES = [
  { cle: 'matin', libelle: 'Matin' },
  { cle: 'apres_midi', libelle: 'Après-midi' },
];

const caseDe = (j, demi) => (j.cases && j.cases[demi]) || { signeLe: null };

/** Les cases encore à signer : toutes les cases vides, passées ou à venir. */
function casesLibres(feuille) {
  return (feuille.journees || []).flatMap((j) =>
    DEMI_JOURNEES.filter((d) => !caseDe(j, d.cle).signeLe).map((d) => ({
      jour: j.jour,
      demi: d.cle,
      libelle: d.libelle,
    }))
  );
}

/**
 * Une case de la feuille entière : signée (horodatage) ou à signer (bouton).
 * Aucune limite de date : chacun signe quand il veut.
 */
function CaseGrille({ j, d, enSignature, envoi, onSigner }) {
  const c = caseDe(j, d.cle);
  const active = enSignature && enSignature.jour === j.jour && enSignature.demi === d.cle;
  if (c.signeLe) {
    return (
      <span className="emargement-grille-signee">
        Signée
        <small>{formatDateTime(c.signeLe)}</small>
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`emargement-grille-signer${active ? ' emargement-grille-signer-active' : ''}`}
      onClick={() => onSigner({ jour: j.jour, demi: d.cle, libelle: d.libelle })}
      disabled={envoi}
      aria-label={`Signer ${jourCourt(j.jour)}, ${d.libelle}`}
    >
      Signer
    </button>
  );
}

/** Largeur du nom + une colonne par demi-journée : ce qu'il faut au tableau pour tenir. */
const LARGEUR_NOM = 160;
const LARGEUR_COLONNE = 126;

/**
 * La feuille entière du signataire, comme l'onglet « Émarger » du back-office :
 * sa ligne, et pour chaque journée deux colonnes — matin, après-midi.
 *
 * Deux dispositions des mêmes cases, choisies d'après la largeur que la page
 * laisse RÉELLEMENT à la grille (et non d'après la taille de l'écran : à côté
 * des cartes d'information, un grand écran peut ne laisser que 900 px) :
 *   - le tableau quand toutes ses colonnes tiennent, sans défilement ;
 *   - sinon un bloc par date, Matin et Après-midi côte à côte. Les blocs se
 *     rangent en plusieurs colonnes quand la place le permet, et sur téléphone
 *     tiennent en une — un tableau y serait coupé après sa première colonne,
 *     sans rien qui dise de le faire défiler.
 */
function GrilleEmargement({ feuille, enSignature, envoi, onSigner }) {
  const journees = feuille.journees || [];
  const props = { enSignature, envoi, onSigner };

  const racine = useRef(null);
  const [largeur, setLargeur] = useState(0);
  // Mesuré avant le premier affichage (layout effect) : pas de saut visible
  // d'une disposition à l'autre.
  useLayoutEffect(() => {
    const el = racine.current;
    if (!el) return undefined;
    setLargeur(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observateur = new ResizeObserver((entrees) => setLargeur(entrees[0].contentRect.width));
    observateur.observe(el);
    return () => observateur.disconnect();
  }, []);

  const tableau = largeur >= LARGEUR_NOM + journees.length * DEMI_JOURNEES.length * LARGEUR_COLONNE;

  return (
    <div ref={racine} className="emargement-grille-racine">
      {tableau ? (
        <div className="emargement-grille-cadre">
          <table className="emargement-grille">
            <thead>
              <tr>
                <th rowSpan={2} className="emargement-grille-nom">
                  Signataire
                </th>
                {journees.map((j) => (
                  <th key={j.jour} colSpan={2}>
                    {jourCourt(j.jour)}
                  </th>
                ))}
              </tr>
              <tr>
                {journees.flatMap((j) =>
                  DEMI_JOURNEES.map((d) => <th key={`${j.jour}-${d.cle}`}>{d.libelle}</th>)
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="emargement-grille-nom">
                  {feuille.signataire.nom}
                  <span>{feuille.signataire.qualite}</span>
                </td>
                {journees.flatMap((j) =>
                  DEMI_JOURNEES.map((d) => (
                    <td key={`${j.jour}-${d.cle}`}>
                      <CaseGrille j={j} d={d} {...props} />
                    </td>
                  ))
                )}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="emargement-grille-blocs">
          <div className="emargement-grille-blocs-nom">
            {feuille.signataire.nom}
            <span>{feuille.signataire.qualite}</span>
          </div>
          {journees.map((j) => (
            <div className="emargement-grille-blocs-jour" key={j.jour}>
              <div className="emargement-grille-blocs-date">{jourCourt(j.jour)}</div>
              <div className="emargement-grille-blocs-cases">
                {DEMI_JOURNEES.map((d) => (
                  <div className="emargement-grille-blocs-case" key={d.cle}>
                    <span className="emargement-grille-blocs-demi">{d.libelle}</span>
                    <CaseGrille j={j} d={d} {...props} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Émargement en ligne, atteint par le lien reçu le matin de la formation.
 *
 * Une page par personne : le jeton désigne le signataire, la page ne montre
 * jamais l'état des autres. Le formateur a la sienne, comme les
 * participants — c'est la même page, seule sa qualité change.
 *
 * Le bloc de signature ouvre la page : on signe sur téléphone, souvent dans
 * la précipitation du matin, et personne ne devine qu'il faut défiler. Le
 * choix de la case, le nom et le cadre tiennent dans le premier écran ; les
 * informations et le récapitulatif viennent après.
 */
function Emargement() {
  const { token } = useParams();
  // Une seule présentation, quel que soit le lien : la feuille entière, avec
  // chaque date et chaque colonne à signer. Un lien « par date et moment »
  // (?jour=aaaa-mm-jj&demi=matin) y présélectionne en plus la case visée, dont
  // le cadre de signature s'ouvre aussitôt ; un lien « toutes les journées »
  // (?toutes=1) n'en présélectionne aucune.
  const [params] = useSearchParams();
  const [feuille, setFeuille] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Journée dont on est en train de signer la case ; null = aucune.
  const [enSignature, setEnSignature] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreurSignature, setErreurSignature] = useState(null);
  // Confirmation après une signature réussie, affichée au-dessus du bloc.
  const [confirmation, setConfirmation] = useState(null);
  // Le nom s'écrit à la main, comme sur une feuille papier : c'est lui qui
  // s'inscrira dans la case, à côté de la date de signature.
  const [nomSaisi, setNomSaisi] = useState('');

  // Le cadre de signature sous la feuille : on y amène le lecteur quand il
  // touche « Signer » dans une colonne, ou quand le lien visait une case.
  const cadreSignatureRef = useRef(null);
  const defilerAuChargement = useRef(false);

  // Le cadre « Notes du formateur » de la feuille : réservé au formateur,
  // repris sur le PDF à chaque enregistrement.
  const [notes, setNotes] = useState('');
  const [notesEnvoi, setNotesEnvoi] = useState(false);
  const [notesEtat, setNotesEtat] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getEmargement(token)
      .then((res) => {
        setFeuille(res.data);
        setNotes(res.data?.notesFormateur || '');
      })
      .catch((err) => setError(err.message || 'Lien invalide.'))
      .finally(() => setLoading(false));
  }, [token]);

  async function enregistrerNotes() {
    setNotesEnvoi(true);
    setNotesEtat(null);
    try {
      const res = await enregistrerNotesFormateur(token, notes);
      setFeuille(res.data);
      setNotes(res.data?.notesFormateur || '');
      setNotesEtat({ type: 'succes', texte: 'Notes enregistrées sur la feuille de présence.' });
    } catch (err) {
      setNotesEtat({ type: 'erreur', texte: err.message || 'Enregistrement impossible.' });
    } finally {
      setNotesEnvoi(false);
    }
  }

  useEffect(() => {
    charger();
  }, [charger]);

  // Le châssis public borne toutes ses pages à 940 px, centrées : bien pour un
  // devis qu'on lit, pas pour une feuille qu'on signe. Tant que cette page est
  // ouverte, le châssis la laisse remplir l'écran (cf. Signature.css).
  useEffect(() => {
    document.body.classList.add('page-emargement');
    return () => document.body.classList.remove('page-emargement');
  }, []);

  // La case désignée par le lien, si elle n'est pas encore signée ;
  // à défaut, l'unique case restante : le cadre se présente tout prêt, sans
  // un geste. Une seule fois, au chargement.
  const [caseProposee, setCaseProposee] = useState(false);
  useEffect(() => {
    if (!feuille || caseProposee) return;
    setCaseProposee(true);
    const libres = casesLibres(feuille);
    const jour = params.get('jour');
    const demi = params.get('demi') === 'apres_midi' ? 'apres_midi' : 'matin';
    const visee = libres.find((c) => c.jour === jour && c.demi === demi);
    const choix = visee || (libres.length === 1 ? libres[0] : null);
    if (!choix) return;
    // Sur téléphone la feuille remplit déjà l'écran : une case visée par le
    // lien ramène le lecteur à son cadre de signature.
    defilerAuChargement.current = Boolean(visee);
    setEnSignature(choix);
    setNomSaisi(feuille.signataire.nom || '');
  }, [feuille, caseProposee, params]);

  useEffect(() => {
    if (!enSignature || !defilerAuChargement.current) return;
    defilerAuChargement.current = false;
    const t = setTimeout(() => {
      if (cadreSignatureRef.current) {
        cadreSignatureRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
    return () => clearTimeout(t);
  }, [enSignature]);

  // Sélection d'une case de la feuille.
  function choisirCase(c) {
    setEnSignature(c);
    setErreurSignature(null);
    // Un nom déjà saisi reste : on ne l'écrase pas en changeant de case.
    setNomSaisi((n) => n || feuille.signataire.nom || '');
  }

  // Une case de la feuille : on la choisit, puis le cadre de signature qui
  // vient d'apparaître dessous passe à l'écran.
  function signerDepuisGrille(c) {
    choisirCase(c);
    requestAnimationFrame(() => {
      if (cadreSignatureRef.current) {
        cadreSignatureRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  async function valider(image) {
    // Le cadre est visible avant tout choix : sans case, on refuse l'envoi.
    if (!enSignature) {
      setErreurSignature('Choisissez d’abord la demi-journée à signer, juste au-dessus.');
      return;
    }
    setEnvoi(true);
    setErreurSignature(null);
    setConfirmation(null);
    try {
      const res = await signerJournee(token, {
        jour: enSignature.jour,
        demiJournee: enSignature.demi,
        image,
        nom: nomSaisi,
      });
      setFeuille(res.data);
      setConfirmation(
        `Signature enregistrée — ${jourLong(enSignature.jour)}, ${enSignature.libelle.toLowerCase()}.`
      );
      setEnSignature(null);
    } catch (err) {
      setErreurSignature(err.message || 'La signature n’a pas pu être enregistrée.');
    } finally {
      setEnvoi(false);
    }
  }

  const toutSigne = (feuille?.journees || []).every((j) =>
    DEMI_JOURNEES.every((d) => caseDe(j, d.cle).signeLe)
  );

  // Le nom, le cadre de signature et la mention de la case choisie.
  const champsSignature = (
    <>
      <label className="signature-champ signature-champ-large">
        <span className="signature-champ-label">Nom et prénom</span>
        <input
          type="text"
          className="signature-champ-input"
          value={nomSaisi}
          onChange={(e) => setNomSaisi(e.target.value)}
          placeholder="Comme il doit figurer sur la feuille"
          maxLength={255}
          disabled={envoi}
        />
      </label>
      {erreurSignature && <div className="signature-etat signature-etat-erreur">{erreurSignature}</div>}
      <SignaturePad onValider={valider} validation={envoi} />
      <p className="emargement-attestation">
        En signant, vous attestez de votre présence à cette demi-journée (datée du jour de
        signature).
      </p>
    </>
  );

  return (
    <div className="devis-page devis-page-scroll signature-doc emargement-page">
      {/* En-tête compact, sur une ligne : le bloc de signature doit tenir
          dans le premier écran d'un téléphone. */}
      <header className="emargement-entete">
        <h1 className="devis-title">Feuille de présence</h1>
        <p className="devis-subtitle">
          {feuille ? feuille.session.intitule : 'Signature en ligne'}
        </p>
      </header>

      {loading && <div className="signature-etat">Chargement de la feuille...</div>}
      {!loading && error && <div className="signature-etat signature-etat-erreur">{error}</div>}

      {!loading && !error && feuille && (
        <div className="emargement-colonnes">
          <div className="emargement-principal">
            {/* ---------- Le geste attendu, en premier : signer ---------- */}

            {confirmation && !toutSigne && (
              <div className="signature-etat signature-etat-succes emargement-confirmation">
                <p>{confirmation}</p>
              </div>
            )}

            {toutSigne && (
              <div className="signature-etat signature-etat-succes emargement-succes">
                <p className="emargement-succes-titre">Tout est signé, merci !</p>
                <p className="emargement-succes-detail">
                  Toutes vos cases sont signées, matin et après-midi. Vous pouvez fermer cette
                  page.
                </p>
              </div>
            )}

            {/* La feuille entière : une ligne, chaque date et chaque demi-journée en
                colonnes, et le cadre de signature sous la case touchée. */}
            <section className="emargement-signer">
              <p className="emargement-consigne">
                Voici toute votre feuille : chaque date, matin et après-midi. Touchez « Signer »
                sous la demi-journée voulue, puis signez dans le cadre qui s’ouvre dessous.
              </p>
              <GrilleEmargement
                feuille={feuille}
                enSignature={enSignature}
                envoi={envoi}
                onSigner={signerDepuisGrille}
              />
              {enSignature && (
                <div className="emargement-grille-signature" ref={cadreSignatureRef}>
                  <p className="emargement-consigne">
                    <strong>
                      {jourCourt(enSignature.jour)} — {enSignature.libelle}
                    </strong>
                  </p>
                  {champsSignature}
                </div>
              )}
            </section>

            {feuille.signataire.estFormateur && (
              <>
                <div className="devis-form-section-title">Notes du formateur</div>
                <p className="signature-mention">
                  Imprimées sur la feuille de présence, modifiables à tout moment depuis ce lien.
                </p>
                <label className="signature-champ signature-champ-large">
                  <span className="signature-champ-label">Vos notes</span>
                  <textarea
                    className="signature-champ-input"
                    rows={8}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    maxLength={4000}
                    disabled={notesEnvoi}
                    placeholder="Ex. : séance complète, un participant absent l'après-midi, questions sur…"
                  />
                </label>
                {notesEtat && (
                  <div
                    className={`signature-etat ${
                      notesEtat.type === 'succes' ? 'signature-etat-succes' : 'signature-etat-erreur'
                    }`}
                  >
                    {notesEtat.texte}
                  </div>
                )}
                <div className="emargement-annuler">
                  <button
                    type="button"
                    className="devis-btn devis-btn-primary"
                    onClick={enregistrerNotes}
                    disabled={notesEnvoi || notes === (feuille.notesFormateur || '')}
                  >
                    {notesEnvoi ? 'Enregistrement…' : 'Enregistrer les notes'}
                  </button>
                  {feuille.notesFormateurLe && (
                    <span className="emargement-journee-etat">
                      Dernier enregistrement : {formatDateTime(feuille.notesFormateurLe)}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ---------- Les informations : à droite sur grand écran, après le geste sur téléphone ---------- */}
          <aside className="emargement-lateral">
            <div className="devis-cartes">
              <div className="devis-carte">
                <div className="devis-carte-titre">SIGNATAIRE</div>
                <dl>
                  <dt>Nom</dt>
                  <dd>{feuille.signataire.nom}</dd>
                  <dt>Qualité</dt>
                  <dd>{feuille.signataire.qualite}</dd>
                </dl>
              </div>
              <div className="devis-carte">
                <div className="devis-carte-titre">SÉANCE</div>
                <dl>
                  <dt>Client</dt>
                  <dd>{feuille.session.societe || '—'}</dd>
                  <dt>Horaires</dt>
                  <dd>{feuille.session.horaires}</dd>
                  <dt>Lieu</dt>
                  <dd>{feuille.session.lieu}</dd>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default Emargement;
